import type { VideoPlatform } from '@/types/forjaNotes';
import { ALLOWED_EMBED_DOMAINS } from '@/types/ensino';

// ─── Platform Detection ───

/**
 * Detect video platform from URL
 */
export function detectVideoPlatform(url: string): VideoPlatform | null {
  if (!url) return null;
  const normalizedUrl = url.toLowerCase();
  if (
    normalizedUrl.includes('youtube.com') ||
    normalizedUrl.includes('youtu.be') ||
    normalizedUrl.includes('youtube-nocookie.com')
  ) return 'youtube';
  if (
    normalizedUrl.includes('tiktok.com') ||
    normalizedUrl.includes('vm.tiktok.com') ||
    normalizedUrl.includes('vt.tiktok.com')
  ) return 'tiktok';
  return null;
}

/**
 * Validate if URL is a valid video URL (YouTube or TikTok)
 */
export function isValidVideoUrl(url: string): boolean {
  return detectVideoPlatform(url) !== null;
}

// ─── YouTube ───

/**
 * Extract YouTube video ID from various URL formats
 */
export function getYouTubeVideoId(url: string): string | null {
  if (!url) return null;
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

/**
 * Generate YouTube embed URL from video ID (with player params)
 */
export function getYouTubeEmbedUrl(videoId: string): string {
  const params = new URLSearchParams({
    modestbranding: '1',
    rel: '0',
    controls: '1',
    disablekb: '0',
    fs: '1',
    playsinline: '1',
    iv_load_policy: '3',
    showinfo: '0',
    cc_load_policy: '0',
  });
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

/**
 * Normalize any YouTube URL to its embed form (from raw URL)
 */
export function getYouTubeEmbedUrlFromUrl(url: string): string | null {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
}

// ─── TikTok ───

/**
 * Extract TikTok video ID from URL
 */
export function getTikTokVideoId(url: string): string | null {
  if (!url) return null;
  const videoMatch = url.match(/video\/(\d+)/);
  if (videoMatch) return videoMatch[1];
  return null;
}

/**
 * Generate TikTok embed URL from video ID
 */
export function getTikTokEmbedUrl(videoId: string): string {
  const params = new URLSearchParams({
    description: '0',
    music_info: '0',
    controls: '1',
    progress_bar: '1',
    play_button: '1',
    rel: '0',
    native_context_menu: '0',
  });
  return `https://www.tiktok.com/player/v1/${videoId}?${params.toString()}`;
}

// ─── Tynk ───

/**
 * Get embed URL for a Tynk player
 */
export function getTynkEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'play.tynk.ai') {
      parsed.searchParams.set('responsive', '1');
      return parsed.toString();
    }
  } catch {}
  return null;
}

/**
 * Check if a URL points to a Tynk embed
 */
export function isTynkEmbed(url: string): boolean {
  try {
    return new URL(url).hostname === 'play.tynk.ai';
  } catch {
    return false;
  }
}

// ─── Bunny Stream (mediadelivery.net) ───

/**
 * Get embed URL for a Bunny Stream player.
 * Normalizes Bunny URLs to the iframe host, which is more compatible with browser embeds.
 */
export function getBunnyStreamEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'player.mediadelivery.net' || parsed.hostname.endsWith('.mediadelivery.net')) {
      const [mode, libraryId, videoId, ...rest] = parsed.pathname.split('/').filter(Boolean);

      parsed.hostname = 'iframe.mediadelivery.net';

      if ((mode === 'play' || mode === 'embed') && libraryId && videoId) {
        const suffix = rest.length ? `/${rest.join('/')}` : '';
        parsed.pathname = `/embed/${libraryId}/${videoId}${suffix}`;
      }

      parsed.searchParams.set('autoplay', 'false');
      return parsed.toString();
    }
  } catch {}
  return null;
}

// ─── Direct Video (Bunny CDN, etc.) ───

const DIRECT_VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov'];

/**
 * Check if URL points to a direct video file
 */
export function isDirectVideoUrl(url: string): boolean {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return DIRECT_VIDEO_EXTENSIONS.some(ext => pathname.endsWith(ext));
  } catch {
    return false;
  }
}

// ─── Embed Domain Allowlist ───

/**
 * Check if URL is in the allowed embed domains
 */
export function isAllowedEmbedDomain(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ALLOWED_EMBED_DOMAINS.some(d => parsed.hostname === d || parsed.hostname.endsWith('.' + d));
  } catch {
    return false;
  }
}

/**
 * Get the appropriate embed URL for a video, or null if not embeddable.
 * Supports YouTube, Tynk, and direct video files (Bunny CDN).
 */
export function getEmbedUrl(url: string): string | null {
  if (!isAllowedEmbedDomain(url)) return null;
  if (isDirectVideoUrl(url)) return url;
  return getYouTubeEmbedUrlFromUrl(url) || getTynkEmbedUrl(url) || getBunnyStreamEmbedUrl(url);
}

// ─── Utilities ───

/**
 * Format duration in seconds to a readable string (e.g., "12:30")
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
